import { EditOutlined, SettingOutlined } from "@ant-design/icons";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Card, Flex, Form, Input, InputNumber, Modal, Radio, Select, Space, Table, Typography } from "antd";
import { useDeferredValue, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useListPage } from "@/components/list/useListPage";
import { useI18n } from "@/i18n";
import { planAllows } from "@/lib/plan";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { PlatformLabel } from "../channels/PlatformBadge";
import { rupiahInput } from "../procurement/common";
import { BulkPriceModal, PriceSyncTag, usePriceErrorText } from "./prices";

type Row = Schemas["PriceSKU"];
type StorePrice = Row["stores"][number];
type Store = Schemas["PriceStore"];
const PROBLEMS = ["", "below_min", "failed", "no_base"] as const;
type Problem = (typeof PROBLEMS)[number];

/** Markup in basis points as "+8%" / "−5%" / "0%". */
function useMarkup() {
  const { formatNumber } = useI18n();
  return (bp: number) => `${bp > 0 ? "+" : bp < 0 ? "−" : ""}${formatNumber(Math.abs(bp) / 100)}%`;
}

/**
 * Harga (App Flow §7): each SKU's base price and minimum, each store's markup
 * over the base, and the price every store sells at, changed one by one or by
 * rule ("Ubah harga massal").
 */
export function Pricing() {
  const { t, formatMoney } = useI18n();
  const navigate = useNavigate();
  const { me, can } = useAuth();
  const manage = can("catalog.manage");
  const markup = useMarkup();
  const page = useListPage();
  const [search, setSearch] = useUrlFilter<string>("q", "");
  const [store, setStore] = useUrlFilter<string>("store", "");
  const [problem, setProblem] = useUrlFilter<Problem>("problem", "", PROBLEMS);
  const q = useDeferredValue(search.trim());
  const [selected, setSelected] = useState<string[]>([]);
  const [pricing, setPricing] = useState<string[] | null>(null);
  const [basing, setBasing] = useState<Row[] | null>(null);
  const [markupOf, setMarkupOf] = useState<Store | null>(null);
  const [priceOf, setPriceOf] = useState<{ sku: Row; store: StorePrice } | null>(null);

  const stores = useQuery({ queryKey: ["prices", "stores"], queryFn: () => unwrap(api.GET("/v1/prices/stores")) });
  const filters = { ...(q ? { q } : {}), ...(store ? { connectionId: store } : {}), ...(problem ? { problem } : {}) };
  const list = useQuery({
    queryKey: ["prices", "skus", filters, page.query],
    queryFn: () => unwrap(api.GET("/v1/prices/skus", { params: { query: { ...filters, ...page.query } } })),
    placeholderData: keepPreviousData,
    // Follow the marketplaces' answers while a change is on its way.
    refetchInterval: (qr) => (qr.state.data?.items.some((r) => r.stores.some((s) => s.priceSync?.status === "pending")) ? 2000 : false),
  });
  const rows = list.data?.items ?? [];
  const money = (v: number | null) => (v == null ? <Typography.Text type="secondary">—</Typography.Text> : <span className="z-num">{formatMoney(v, "IDR")}</span>);

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space orientation="vertical" size={0}>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
            {t("pricing.title")}
          </Typography.Title>
          <Typography.Text type="secondary">{t("pricing.subtitle")}</Typography.Text>
        </Space>
        <Space wrap>
          {can("finance.view") && planAllows(me?.workspace?.plan, "finance") && <Button onClick={() => navigate("/app/finance/margins")}>{t("pricing.margins")}</Button>}
          {can("inventory.view") && <Button onClick={() => navigate("/app/stock-sync?tab=campaigns")}>{t("pricing.promos")}</Button>}
        </Space>
      </Flex>

      <Card size="small" title={t("pricing.markups")} style={{ marginBottom: 16 }} loading={stores.isPending}>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
          {t("pricing.markupsHelp")}
        </Typography.Paragraph>
        <Flex gap={8} wrap>
          {(stores.data?.items ?? []).map((s) => (
            <Space key={s.connectionId} size={6} style={{ border: "1px solid var(--ant-color-border-secondary)", borderRadius: 6, padding: "4px 8px" }}>
              <PlatformLabel platform={s.platform} />
              <span>{s.name}</span>
              <strong className="z-num">{markup(s.markupBp)}</strong>
              {manage && (
                <Button size="small" type="text" icon={<SettingOutlined />} onClick={() => setMarkupOf(s)} aria-label={t("pricing.markupEdit", { store: s.name })} />
              )}
            </Space>
          ))}
          {stores.data?.items.length === 0 && <Typography.Text type="secondary">{t("margin.noStores")}</Typography.Text>}
        </Flex>
      </Card>

      <Flex gap={12} wrap justify="space-between" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input.Search allowClear placeholder={t("catalog.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 280 }} />
          <Select<string>
            value={store}
            onChange={setStore}
            style={{ width: 200 }}
            options={[{ value: "", label: t("price.storesAll") }, ...(stores.data?.items ?? []).map((s) => ({ value: s.connectionId, label: s.name }))]}
          />
          <Select<Problem>
            value={problem}
            onChange={setProblem}
            style={{ width: 220 }}
            options={PROBLEMS.map((p) => ({ value: p, label: t(p ? `pricing.problem.${p}` : "pricing.problem.all") }))}
          />
        </Space>
        {manage && (
          <Space wrap>
            <Button disabled={selected.length === 0} onClick={() => setBasing(rows.filter((r) => selected.includes(r.id)))}>
              {t("pricing.setBaseN", { n: selected.length })}
            </Button>
            <Button type="primary" disabled={selected.length === 0} onClick={() => setPricing(selected)}>
              {t("price.bulk", { n: selected.length })}
            </Button>
          </Space>
        )}
      </Flex>

      <Table<Row>
        rowKey="id"
        loading={list.isFetching && !list.data}
        dataSource={rows}
        pagination={page.pagination(list.data)}
        scroll={{ x: 1000 }}
        locale={{ emptyText: t("pricing.empty") }}
        rowSelection={manage ? { selectedRowKeys: selected, onChange: (keys) => setSelected(keys as string[]) } : undefined}
        columns={[
          {
            title: t("catalog.code"),
            width: 240,
            render: (_, r) => (
              <Space orientation="vertical" size={0}>
                <Typography.Link className="z-num" onClick={() => navigate(`/app/catalog/${r.id}`)}>
                  {r.code}
                </Typography.Link>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {r.name}
                </Typography.Text>
              </Space>
            ),
          },
          { title: t("pricing.base"), dataIndex: "basePrice", width: 130, align: "right", render: money },
          { title: t("pricing.min"), dataIndex: "minPrice", width: 130, align: "right", render: money },
          {
            title: t("pricing.onStores"),
            render: (_, r) =>
              r.stores.length === 0 ? (
                <Typography.Text type="secondary">{t("pricing.notSold")}</Typography.Text>
              ) : (
                <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                  {r.stores.map((s) => {
                    const under = r.minPrice != null && s.price != null && s.price < r.minPrice;
                    return (
                      <Flex key={s.channelSkuId} gap={8} align="center" wrap>
                        <PlatformLabel platform={s.platform} />
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {[s.shopName, s.variant].filter(Boolean).join(" · ")}
                        </Typography.Text>
                        <Typography.Text strong className="z-num" type={under ? "danger" : undefined}>
                          {s.price == null ? "—" : formatMoney(s.price, "IDR")}
                        </Typography.Text>
                        <PriceSyncTag p={s} />
                        {manage && (
                          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => setPriceOf({ sku: r, store: s })} aria-label={t("price.edit")} />
                        )}
                      </Flex>
                    );
                  })}
                </Space>
              ),
          },
          ...(manage
            ? [
                {
                  width: 90,
                  render: (_: unknown, r: Row) => (
                    <Button size="small" onClick={() => setBasing([r])}>
                      {t("common.edit")}
                    </Button>
                  ),
                },
              ]
            : []),
        ]}
      />

      <BulkPriceModal
        skuIds={pricing}
        onClose={() => {
          setPricing(null);
          setSelected([]);
        }}
      />
      {basing && (
        <BaseDialog
          rows={basing}
          stores={stores.data?.items ?? []}
          onClose={(saved) => {
            setBasing(null);
            if (saved) setSelected([]);
          }}
        />
      )}
      {markupOf && <MarkupDialog store={markupOf} onClose={() => setMarkupOf(null)} />}
      {priceOf && <StorePriceDialog sku={priceOf.sku} store={priceOf.store} onClose={() => setPriceOf(null)} />}
    </>
  );
}

type Source = "keep" | "value" | "store" | "clear";

/**
 * Base price and minimum of one SKU or several: a number, the price one store
 * sells at (base only), cleared, or left as it is.
 */
function BaseDialog({ rows, stores, onClose }: { rows: Row[]; stores: Store[]; onClose: (saved: boolean) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const one = rows.length === 1 ? rows[0] : null;
  const [baseFrom, setBaseFrom] = useState<Source>(one ? "value" : "keep");
  const [base, setBase] = useState<number | null>(one?.basePrice ?? null);
  const [fromStore, setFromStore] = useState<string>();
  const [minFrom, setMinFrom] = useState<Source>(one ? "value" : "keep");
  const [min, setMin] = useState<number | null>(one?.minPrice ?? null);

  const items = rows.map((r) => {
    const pick = (from: Source, value: number | null, current: number | null) =>
      from === "keep" ? current : from === "clear" ? null : from === "store" ? (r.stores.find((s) => s.connectionId === fromStore)?.price ?? current) : value;
    return { skuId: r.id, basePrice: pick(baseFrom, base, r.basePrice), minPrice: pick(minFrom, min, r.minPrice) };
  });
  const ready = (baseFrom !== "value" || (base ?? 0) >= 100) && (minFrom !== "value" || (min ?? 0) >= 100) && (baseFrom !== "store" || !!fromStore);
  const save = useMutation({
    mutationFn: () => unwrap(api.PUT("/v1/prices/skus", { body: { items } })),
    onSuccess: () => {
      void message.success(t("pricing.baseSaved"));
      void queryClient.invalidateQueries({ queryKey: ["prices"] });
      onClose(true);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const money = { min: 100, max: 100_000_000_000, precision: 0, prefix: "Rp", style: { width: 200 }, ...rupiahInput };
  const sources = (withStore: boolean) =>
    (["value", ...(withStore ? (["store"] as const) : []), "clear", ...(one ? [] : (["keep"] as const))] as const).map((s) => ({ value: s, label: t(`pricing.from.${s}`) }));

  return (
    <Modal
      open
      title={one ? t("pricing.setBaseOne", { code: one.code }) : t("pricing.setBaseMany", { n: rows.length })}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !ready }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={() => onClose(false)}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark={false}>
        <Form.Item label={t("pricing.base")} extra={t("pricing.baseHelp")}>
          <Space orientation="vertical">
            <Radio.Group optionType="button" value={baseFrom} onChange={(e) => setBaseFrom(e.target.value as Source)} options={sources(true)} />
            {baseFrom === "value" && <InputNumber<number> {...money} autoFocus value={base} onChange={setBase} />}
            {baseFrom === "store" && (
              <Select<string>
                style={{ width: 260 }}
                placeholder={t("price.stores")}
                value={fromStore}
                onChange={setFromStore}
                options={stores.map((s) => ({ value: s.connectionId, label: s.name }))}
              />
            )}
          </Space>
        </Form.Item>
        <Form.Item label={t("pricing.min")} extra={t("pricing.minHelp")}>
          <Space orientation="vertical">
            <Radio.Group optionType="button" value={minFrom} onChange={(e) => setMinFrom(e.target.value as Source)} options={sources(false)} />
            {minFrom === "value" && <InputNumber<number> {...money} value={min} onChange={setMin} />}
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}

/** A store's markup over the base price, in percent on screen and basis points on the wire. */
function MarkupDialog({ store, onClose }: { store: Store; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const [pct, setPct] = useState<number | null>(store.markupBp / 100);
  const save = useMutation({
    mutationFn: () =>
      unwrap(api.PUT("/v1/prices/stores/{connectionID}", { params: { path: { connectionID: store.connectionId } }, body: { markupBp: Math.round((pct ?? 0) * 100) } })),
    onSuccess: () => {
      void message.success(t("pricing.markupSaved"));
      void queryClient.invalidateQueries({ queryKey: ["prices"] });
      onClose();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open
      title={t("pricing.markupEdit", { store: store.name })}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Space orientation="vertical" size={8} style={{ width: "100%" }}>
        <Typography.Text type="secondary">{t("pricing.markupDialogHelp")}</Typography.Text>
        <InputNumber<number> autoFocus min={-50} max={100} precision={2} suffix="%" value={pct} onChange={setPct} style={{ width: 160 }} />
      </Space>
    </Modal>
  );
}

/** One store's price of one SKU, sent to the marketplace. */
function StorePriceDialog({ sku, store, onClose }: { sku: Row; store: StorePrice; onClose: () => void }) {
  const { t, formatMoney } = useI18n();
  const { message } = App.useApp();
  const priceErrorText = usePriceErrorText();
  const queryClient = useQueryClient();
  const [price, setPrice] = useState<number | null>(store.price);
  const send = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/prices", { body: { items: [{ channelSkuId: store.channelSkuId, price: price! }] } })),
    onSuccess: () => {
      void message.success(t("price.queued"));
      for (const key of ["prices", "platform-skus"]) void queryClient.invalidateQueries({ queryKey: [key] });
      onClose();
    },
    onError: (err) => void message.error(priceErrorText(err)),
  });
  const under = sku.minPrice != null && price != null && price < sku.minPrice;
  return (
    <Modal
      open
      title={t("pricing.storePrice", { code: sku.code, store: store.shopName })}
      okText={t("price.save")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: !price || price < 100 || under }}
      confirmLoading={send.isPending}
      onOk={() => send.mutate()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Form layout="vertical" requiredMark={false}>
        <Form.Item
          label={[store.listingName, store.variant].filter(Boolean).join(" · ")}
          validateStatus={under ? "error" : undefined}
          help={under ? t("price.belowFloorIs", { min: formatMoney(sku.minPrice!, "IDR") }) : undefined}
        >
          <InputNumber<number> autoFocus min={100} max={100_000_000_000} precision={0} prefix="Rp" style={{ width: 220 }} value={price} onChange={setPrice} {...rupiahInput} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
